import { where, or, and } from 'firebase/firestore'
import { cloneDeep } from 'lodash'

import FirestoreService from '../services/firestoreService'

import { getConvertedData } from '../models/interface.helper'

import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'
import { IRegistrationTicketInterface } from '../models/registration-tickets/registrationTicket.interface'
import { IEventReviewPublish } from '../models/event-review-publish/event-review-publish.interface'
import {
  IRegisterTab,
  IRegistrationTabs,
  ITeamMemberR,
} from '../models/event-registered-users/event-registered-users.interface'

import { RegistrationByDayModel } from '../models/registrations-by-day/registrationByDay.model'
import { RegistrationTicketModel } from '../models/registration-tickets/registrationTicket.model'
import { RegistrationFeesModel } from '../models/registration-fees/registrationFees.model'
import { IUserPublicInterface } from '../models/users-public/users-public.interface'
import { IRiderTeamMemberInterface } from '../models/rider-team-member/riderTeamMember.interface'
import { IPaperworkTeam } from '../models/user-documents/user-documents.interface'
import { IRegistrationFees } from '../components/modals/AddClasses'

import { daysOfWeek } from './time'

import { CONST } from '../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const listenRegistrationItems = ({
  registrationId,
  ownerId,
  type,
  setRegistrationsByDay,
  setRegistrationsFees,
  setRegistrationsTickets,
}: {
  ownerId?: string
  registrationId: string
  type: ('classes' | 'fees' | 'tickets')[]
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  setRegistrationsFees: (value: IRegistrationFeesInterface[]) => void
  setRegistrationsTickets: (value: IRegistrationTicketInterface[]) => void
}) => {
  if (type.includes('classes')) {
    FirestoreService.listenItems({
      collection_name: COLLECTIONS.REGISTRATION_BY_DAY.NAME,
      constraints: and(
        where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
        ...(ownerId
          ? [
              or(
                where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.RIDER_ID.KEY, '==', ownerId),
                where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.RECIPIENT_ID.KEY, '==', ownerId)
              ),
            ]
          : [])
      ),
      callback: (snaps) => {
        const registrationsByDay: IRegistrationByDayInterface[] = []
        snaps.docs.forEach((currDoc) => {
          const current = getConvertedData(
            RegistrationByDayModel.fromFirestoreDoc(currDoc).toObject()
          )
          if (!current.isDivision) registrationsByDay.push(current)
        })
        setRegistrationsByDay(registrationsByDay)
      },
    })
  }

  if (type.includes('fees')) {
    FirestoreService.listenItems({
      collection_name: COLLECTIONS.REGISTRATION_FEES.NAME,
      constraints: and(
        where(COLLECTIONS.REGISTRATION_FEES.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
        ...(ownerId
          ? [
              or(
                where(COLLECTIONS.REGISTRATION_FEES.FIELDS.OWNER_ID.KEY, '==', ownerId),
                where(COLLECTIONS.REGISTRATION_FEES.FIELDS.RECIPIENT_ID.KEY, '==', ownerId)
              ),
            ]
          : [])
      ),
      callback: (snaps) => {
        const registrationsFees: IRegistrationFeesInterface[] = []
        snaps.docs.forEach((currDoc) => {
          const registrationFee = getConvertedData(
            RegistrationFeesModel.fromFirestoreDoc(currDoc).toObject()
          )
          if (registrationFee.selectedUnitsCount > 0) registrationsFees.push(registrationFee)
        })
        setRegistrationsFees(registrationsFees)
      },
    })
  }

  if (type.includes('tickets')) {
    FirestoreService.listenItems({
      collection_name: COLLECTIONS.REGISTRATION_TICKET.NAME,
      constraints: and(
        where(COLLECTIONS.REGISTRATION_TICKET.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
        ...(ownerId
          ? [
              or(
                where(COLLECTIONS.REGISTRATION_TICKET.FIELDS.OWNER_ID.KEY, '==', ownerId),
                where(COLLECTIONS.REGISTRATION_TICKET.FIELDS.RECIPIENT_ID.KEY, '==', ownerId)
              ),
            ]
          : [])
      ),
      callback: (snaps) => {
        const registrationsTickets: IRegistrationTicketInterface[] = []
        snaps.docs.forEach((currDoc) => {
          registrationsTickets.push(
            getConvertedData(RegistrationTicketModel.fromFirestoreDoc(currDoc).toObject())
          )
        })
        setRegistrationsTickets(registrationsTickets)
      },
    })
  }
}

export const getRegistrationEventDataInDb = async ({
  registrationId,
  ownerId,
  type,
}: {
  ownerId?: string | null
  registrationId?: string
  type: ('classes' | 'fees' | 'tickets')[]
}) => {
  let registrationsFees: IRegistrationFeesInterface[] = []
  let registrationsByDay: IRegistrationByDayInterface[] = []
  let registrationsTickets: IRegistrationTicketInterface[] = []
  let publishedEventInDb: IEventReviewPublish | null = null

  if (type.includes('classes')) {
    const commonSnaps = await FirestoreService.filterItems(COLLECTIONS.REGISTRATION_BY_DAY.NAME, [
      and(
        ...(registrationId
          ? [
              where(
                COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.REGISTRATION_DOC_ID.KEY,
                '==',
                registrationId
              ),
            ]
          : []),
        ...(ownerId
          ? [
              or(
                where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.RIDER_ID.KEY, '==', ownerId),
                where(COLLECTIONS.REGISTRATION_BY_DAY.FIELDS.RECIPIENT_ID.KEY, '==', ownerId)
              ),
            ]
          : [])
      ),
    ])

    commonSnaps.docs.forEach((currDoc) => {
      const current = getConvertedData(RegistrationByDayModel.fromFirestoreDoc(currDoc).toObject())
      if (!current.isDivision) registrationsByDay.push(current)
    })
  }

  if (type.includes('tickets')) {
    const commonSnaps = await FirestoreService.filterItems(COLLECTIONS.REGISTRATION_TICKET.NAME, [
      and(
        ...(registrationId
          ? [
              where(
                COLLECTIONS.REGISTRATION_TICKET.FIELDS.REGISTRATION_DOC_ID.KEY,
                '==',
                registrationId
              ),
            ]
          : []),
        ...(ownerId
          ? [
              or(
                where(COLLECTIONS.REGISTRATION_TICKET.FIELDS.OWNER_ID.KEY, '==', ownerId),
                where(COLLECTIONS.REGISTRATION_TICKET.FIELDS.RECIPIENT_ID.KEY, '==', ownerId)
              ),
            ]
          : [])
      ),
    ])

    commonSnaps.docs.forEach((currDoc) => {
      registrationsTickets.push(
        getConvertedData(RegistrationTicketModel.fromFirestoreDoc(currDoc).toObject())
      )
    })
  }

  if (type.includes('fees')) {
    const commonSnaps = await FirestoreService.filterItems(COLLECTIONS.REGISTRATION_FEES.NAME, [
      and(
        ...(registrationId
          ? [
              where(
                COLLECTIONS.REGISTRATION_FEES.FIELDS.REGISTRATION_DOC_ID.KEY,
                '==',
                registrationId
              ),
            ]
          : []),
        ...(ownerId
          ? [
              or(
                where(COLLECTIONS.REGISTRATION_FEES.FIELDS.OWNER_ID.KEY, '==', ownerId),
                where(COLLECTIONS.REGISTRATION_FEES.FIELDS.RECIPIENT_ID.KEY, '==', ownerId)
              ),
            ]
          : [])
      ),
    ])

    commonSnaps.docs.forEach((currDoc) => {
      const registrationFee = getConvertedData(
        RegistrationFeesModel.fromFirestoreDoc(currDoc).toObject()
      )
      if (registrationFee.selectedUnitsCount > 0) registrationsFees.push(registrationFee)
    })
  }

  return { registrationsByDay, registrationsFees, registrationsTickets, publishedEventInDb }
}

export function mergeRegistrationWithTabData(
  registerTabData: IRegistrationTabs['IRegisterTab'][],
  registrationsByDay: IRegistrationByDayInterface[]
) {
  let registerTabDataCloned = cloneDeep(registerTabData)
  let registrationsByDayCloned = cloneDeep(registrationsByDay)
  registerTabDataCloned = registerTabDataCloned.map((currRow) => {
    return {
      ...currRow,
      registrationsByDay: [],
    }
  })
  return registerTabDataCloned.map((item) => {
    const registrationsByDay = registrationsByDayCloned.filter(
      (obj) => obj.uuid === item.uuid && obj.registrationByDayName === item.registrationByDayName
    )

    const newChildren = cloneDeep(item?.children)?.map((child) => {
      const registrationsByDayChildren = registrationsByDayCloned.filter(
        (obj) => obj.uuid === child.uuid
      )
      return { ...child, registrationsByDay: registrationsByDayChildren }
    })

    return { ...item, registrationsByDay, children: newChildren }
  })
}

export const isValidRegistrationEventItems = ({
  registrationsByDay,
  registrationsFees,
  registrationsTickets,
}: {
  registrationsByDay: IRegistrationByDayInterface[]
  registrationsFees: IRegistrationFeesInterface[]
  registrationsTickets: IRegistrationTicketInterface[]
}) => {
  const allRecipientsAssigned =
    registrationsByDay.every((item) => item.recipientId) &&
    registrationsFees.every((item) => item.recipientId) &&
    registrationsTickets.every((item) => item.recipientId)

  const allOwnersAssigned =
    registrationsByDay.every((item) => item.riderId) &&
    registrationsFees.every((item) => item.ownerId) &&
    registrationsTickets.every((item) => item.ownerId)

  return { allRecipientsAssigned, allOwnersAssigned }
}

export function sortUsersByRegistrar(users: IUserPublicInterface[], ownerId?: string | null) {
  return [...users].sort((a, b) => {
    if (a.id === ownerId) return -1
    if (b.id === ownerId) return 1
    return 0
  })
}
export function sortTeamMembersByRegistrar(users: ITeamMemberR[], ownerId?: string | null) {
  return [...users].sort((a, b) => {
    if (a.memberId === ownerId) return -1
    if (b.memberId === ownerId) return 1
    return 0
  })
}

export const sortByRiderRole = (
  ridersTeamMembers: (IRiderTeamMemberInterface | IPaperworkTeam)[]
) => {
  return ridersTeamMembers.sort((a, b) => {
    return a.teamMemberRole === 'Rider' ? -1 : b.teamMemberRole === 'Rider' ? 1 : 0
  })
}

export const getDays = (classes: (IRegisterTab | IRegistrationFees)[]) => {
  const allDays: string[] = []
  const allDates: { day: string; date: string }[] = []
  const sorted = cloneDeep(classes || [])?.sort(
    (a, b) => new Date(a?.startDate || 0).getTime() - new Date(b?.startDate || 0).getTime()
  )
  sorted?.forEach((fee) => {
    const day = daysOfWeek[new Date(fee.startDate)?.getDay()]
    if (!allDays.includes(day)) {
      allDays.push(day)
      allDates.push({ day, date: new Date(fee.startDate).toDateString() })
    }
    fee.children?.forEach((child) => {
      const day = daysOfWeek[new Date(child.startDate)?.getDay()]
      if (!allDays.includes(day)) {
        allDays.push(day)
        allDates.push({ day, date: new Date(child.startDate).toDateString() })
      }
    })
  })

  return { allDays, allDates }
}
